import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { AssociationsAndSuppliers } from '@/components/AssociationsAndSuppliers'
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

// Types for images
interface ImageItem {
  src: string
  alt: string
}

export default function FencesGallery() {
  // State for images
  const [displayedImages, setDisplayedImages] = useState<ImageItem[]>([])
  
  // State for lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxSlide, setLightboxSlide] = useState(0)
  const [lightboxAnimation, setLightboxAnimation] = useState('entering') // 'entering', 'visible', 'exiting'
  
  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  // Function to parse image filename into alt text
  function parseImageFileName(filename: string): string {
    // Remove file extension
    const withoutExtension = filename.substring(0, filename.lastIndexOf('.'))
    // Replace hyphens with spaces
    return withoutExtension.replace(/-/g, ' ')
  }

  // Function to fix path for production and development consistency
  function fixImagePath(path: string): string {
    // Remove /public prefix if it exists (for dev mode)
    let imagePath = path.replace('/public', '')
    
    // Ensure path starts with a slash
    if (!imagePath.startsWith('/')) {
      imagePath = '/' + imagePath
    }
    
    return imagePath
  }

  // Load images dynamically
  useEffect(() => {
    try {
      // Using Vite's import.meta.glob to get all images
      const imageModules = import.meta.glob('/assets/images/Fence/**/*.{jpg,jpeg,png,webp}', { eager: true })
      
      const loadedImages: ImageItem[] = []

      // Convert the modules to ImageItem objects
      Object.keys(imageModules).forEach(path => {
        const imagePath = fixImagePath(path)
        
        // Get filename for alt text
        const fileName = path.substring(path.lastIndexOf('/') + 1)
        const altText = parseImageFileName(fileName)
        
        loadedImages.push({
          src: imagePath,
          alt: altText
        })
      })

      setDisplayedImages(loadedImages)
    } catch (error) {
      console.error('Error loading images:', error)
    }
  }, [])
  
  // Lightbox functions
  const openLightbox = (index: number) => {
    setLightboxSlide(index)
    setLightboxOpen(true)
    setLightboxAnimation('entering')
    // Start animation
    setTimeout(() => setLightboxAnimation('visible'), 50)
    // Prevent body scrolling when lightbox is open
    document.body.style.overflow = 'hidden'
  }
  
  const closeLightbox = () => {
    // Start exit animation
    setLightboxAnimation('exiting')
    // Wait for animation to finish before fully closing
    setTimeout(() => {
      setLightboxOpen(false)
      // Restore body scrolling
      document.body.style.overflow = 'visible'
    }, 300) // Should match the animation duration
  }
  
  const goToPrevLightboxSlide = () => {
    setLightboxSlide((prev) => 
      prev === 0 ? displayedImages.length - 1 : prev - 1
    )
  }
  
  const goToNextLightboxSlide = () => {
    setLightboxSlide((prev) => 
      (prev + 1) % displayedImages.length
    )
  }
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return
      
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevLightboxSlide()
          break
        case 'ArrowRight':
          goToNextLightboxSlide()
          break
        case 'Escape':
          closeLightbox()
          break
        default:
          break
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen, displayedImages.length])

  // Consolidated Schema Data (Local Business, FAQ, Breadcrumb)
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Services",
            "item": "https://busybeaverconstruction.com/services/"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Fences",
            "item": "https://busybeaverconstruction.com/services/fences/"
          }
        ]
      },
      {
        "@type": "LocalBusiness",
        "name": "Busy Beaver Construction",
        "image": "https://busybeaverconstruction.com/assets/images/Fence/Cedar-Custom-Horizontal-fence-1x6-_-1x2-pickets.jpg",
        "@id": "https://busybeaverconstruction.com",
        "url": "https://busybeaverconstruction.com/services/fences",
        "telephone": "403-286-5400",
        "description": "Professional fence builders in Calgary. Specializing in custom wood, vinyl, chain link, and ornamental steel fencing installation.",
        "areaServed": {
          "@type": "City",
          "name": "Calgary",
          "addressRegion": "AB",
          "addressCountry": "CA"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Fence Installation Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Wood Fence Installation"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Vinyl Fence Installation"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Ornamental Steel Fence Installation"
              }
            }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Do I need a building permit for a new fence in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "You do not need a building permit for a backyard fence if it is under 2 meters (6 feet 6 inches) in height. Fences in the front yard must not exceed 1.2 meters (4 feet). If your design exceeds these heights, or if you live on a corner lot with visibility restrictions, a permit is required."
            }
          },
          {
            "@type": "Question",
            "name": "How deep do fence posts need to be in Alberta?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Fence posts in Calgary must be set below the frost line to prevent frost heave. We drill post holes to a depth of 36 to 48 inches and secure them with concrete, ensuring the fence remains plumb and stable during extreme temperature fluctuations."
            }
          },
          {
            "@type": "Question",
            "name": "Who is responsible for the cost of a shared property line fence?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "In Alberta, if a fence is built exactly on the property line, it is considered a shared asset. Neighbors typically split the cost 50/50. However, agreement must be reached before construction. If a neighbor declines to pay, you must build the fence entirely within your own property line at your own expense."
            }
          },
          {
            "@type": "Question",
            "name": "What is the most durable fence material for Calgary weather?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ornamental steel and high-quality vinyl are the most durable, low-maintenance options. For wood fences, Western Red Cedar naturally resists moisture and rot better than untreated wood, though pressure-treated wood offers excellent long-term durability when properly maintained."
            }
          },
          {
            "@type": "Question",
            "name": "How long does it take to build a residential fence?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "A standard residential wood or vinyl fence takes between 3 to 5 days to complete, depending on total linear footage and weather conditions. This timeline includes demolition of the old fence, post setting, concrete curing time, and final panel installation."
            }
          },
          {
            "@type": "Question",
            "name": "How far back from the property line does a fence need to be built?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "If you are not splitting the cost with a neighbor to build on the shared line, your fence must be built entirely within your own property boundaries. There is no mandatory setback distance, but you must ensure footings do not encroach over the line. A real property report (RPR) is recommended to locate property pins accurately."
            }
          },
          {
            "@type": "Question",
            "name": "Do Cedar fences need to be treated immediately after installation?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No. New Western Red Cedar needs time to cure and weather before applying treatments. We advise homeowners to wait at least 6 to 8 weeks during the dry summer months. Staining the wood immediately can trap internal moisture, causing the stain to blister and peel prematurely."
            }
          },
          {
            "@type": "Question",
            "name": "How do you build fences to withstand Calgary wind storms?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We mitigate wind shear by utilizing heavy-duty 4x4 or 4x6 posts set deep in high-PSI concrete footings. For solid wood or vinyl privacy fences built in high-wind corridors, we adjust the post spacing and structural framework to prevent panel blowout during extreme gusts."
            }
          },
          {
             "@type": "Question",
             "name": "How much does it cost to build a fence in Calgary?",
             "acceptedAnswer": {
               "@type": "Answer",
               "text": "Fence cost in Calgary varies widely based on linear footage, material choice, and soil conditions. Chain link is the most economical, followed by pressure-treated wood, cedar, vinyl, and ornamental steel. We provide a free estimate and fence calculator consultation to give you an exact quote for your specific project."
             }
          },
          {
             "@type": "Question",
             "name": "Do you offer fence financing?",
             "acceptedAnswer": {
               "@type": "Answer",
               "text": "Yes, we partner with Financeit to offer flexible fence financing options for homeowners. You can finance your new fence installation for as low as $99 a month, making backyard upgrades highly accessible."
             }
          }
        ]
      }
    ]
  };

  return (
    <main>
      <Helmet>
        <title>Calgary Fence Company & Contractors | Busy Beaver</title>
        <meta name="description" content="Calgary's premier custom deck and fence builder for 25+ years. Quality outdoor living spaces built by professionals to transform your backyard dreams into reality." />
        <meta name="keywords" content="fence builders calgary, calgary fence company, custom fence installation calgary, cedar fence contractors, vinyl fencing calgary, ameristar montage calgary, fence gallery, privacy fences, fence cost calgary, fence financing" />
        <link rel="canonical" href="https://busybeaverconstruction.com/services/fences/" />
        <meta property="og:title" content="Calgary Fence Company & Contractors | Busy Beaver" />
        <meta property="og:description" content="Calgary's premier custom deck and fence builder for 25+ years. Quality outdoor living spaces built by professionals to transform your backyard dreams into reality." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://busybeaverconstruction.com/services/fences/" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Fence/Cedar-Custom-Horizontal-fence-1x6-_-1x2-pickets.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preload" href="/assets/images/Fence/Cedar-Custom-Horizontal-fence-1x6-_-1x2-pickets.jpg" as="image" />
      </Helmet>

      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <Header />
      
      {/* Hero Banner Redesigned to match target image */}
      <section className="relative min-h-[500px] md:min-h-[600px] flex items-center justify-center py-20">
        <img 
          src="/assets/images/Fence/Cedar-Custom-fence-1x6-pickets-with-1x2-Horizontal-Custom-Lattice-top.jpg"
          alt="Custom cedar fence with 1x6 pickets and 1x2 horizontal custom lattice top by Busy Beaver Construction Calgary"
          className="absolute inset-0 w-full h-full object-cover loading-eager"
          fetchPriority="high"
        />        
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-black/40 md:bg-black/50"></div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto flex flex-col items-center mt-12 md:mt-24">
          
          {/* Badge */}
          <div className="inline-block border border-beaver-green bg-black/30 rounded-full py-1.5 px-6 mb-6">
            <p className="text-beaver-green text-sm md:text-base font-bold uppercase tracking-wider">
              Transform Your Outdoors
            </p>
          </div>

          {/* Main Title - ONLY H1 ON PAGE */}
          <h1 className="text-6xl md:text-7xl lg:text-[90px] text-white font-script mb-6 drop-shadow-lg leading-tight">
            Calgary Fence Builders
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-white/95 leading-relaxed font-text mb-10 max-w-3xl drop-shadow-md">
            Expertly engineered cedar, vinyl, and steel fencing built to withstand Alberta's climate. Quality workmanship that ensures lasting value. Serving Calgary, Airdrie, Okotoks, and Chestermere.
          </p>

          {/* CTA Button */}
          <a
            href="/fence-estimate" 
            className="inline-flex items-center justify-center bg-beaver-green text-black font-bold py-3.5 px-8 hover:bg-white transition-colors duration-300 uppercase tracking-wider text-base rounded-sm shadow-xl"
            onClick={(e) => handleNavigation(e, '/fence-estimate')}
          >
            GET YOUR FREE FENCE QUOTE <i className="fas fa-arrow-right ml-3 text-lg"></i>
          </a>

        </div>
      </section>

      {/* Original Content Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center mb-8 md:mb-14">
            <h2 className="text-center text-5xl lg:text-6xl font-primary">Fence Installation & Repair Services</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div className="space-y-6 text-base text-gray-500 leading-relaxed">
              <p>
                A well designed fence enhances your landscape as a beautiful backdrop, and creates privacy and security for your backyard space. 
                It adds additional value and extra curb appeal to update the look and feel of your home's exterior. Whether you need a new installation or reliable fence repair, we have you covered.
              </p>
              
              <p>
                We understand the importance of designing all the elements of fence construction to reflect your needs and personality. 
                We are experienced in installing fences in a variety of styles, materials and budget ranges throughout Calgary neighborhoods like Silverado, Mahogany, and Tuscany.
              </p>
              
              <p>
                <strong>Wood Fence Installation</strong> – Our solid wood fences provide maximum privacy to your backyard, and are built with the finest 
                Pressure-Treated Wood or Cedar fencing products. The most common type of wood fence is the standard vertical picket, Fortress Style. 
                For a more distinctive modern appeal, Horizontal fences offer a great alternative to the traditional style.
              </p>
              
              <p>
                <strong>Vinyl Fence Installation</strong> – Although more expensive than other fencing options, a vinyl fence is extremely durable and virtually 
                maintenance free. Vinyl fencing comes in various color and style options which are attractive to the eye, and can easily 
                compliment any home exterior style or color.
              </p>
              
              <p>
                <strong>Chain Link Fence Installation</strong> – Generally seen along the back property of a home that faces a green space. Chain link fencing is 
                one of the more popular fencing choices for homeowners on a budget, or not looking to create privacy on their property.
              </p>
              
              <p>
                <strong>Ornamental Fence</strong> – Ornamental fences have been the worldwide architectural choice for attractive security on commercial 
                and residential properties. We install Ameristar Montage line of ornamental fences, which is the leader in residential steel fencing. 
              </p>
              
            </div>
            
            <div className="space-y-6">
              <div className="overflow-hidden h-[280px] md:h-[280px]">
                <img 
                  src="/assets/images/Fence/Cedar-Custom-Horizontal-fence-1x6-_-1x2-pickets.jpg"
                  alt="Custom cedar horizontal fence with 1x6 and 1x2 pickets installation by Busy Beaver Construction Calgary"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[280px] md:h-[280px]">
                <img 
                  src="/assets/images/2020/04/Decoartive-picket-top-Vinyl-Fence-Chestnut-Brown.jpg"
                  alt="Decorative picket top vinyl fence in Chestnut Brown by Busy Beaver Construction Calgary"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[280px] md:h-[280px]">
                <img 
                  src="/assets/images/2020/04/Beige-and-white-Lattice-Top-Vinyl-Fence.jpg"
                  alt="Beige and white lattice top vinyl fence installation by Busy Beaver Construction Calgary"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expanded SEO Content Section - Highly Visual & Scannable */}
      <section className="py-16 md:py-24 bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto px-4 max-w-6xl">
          
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-primary text-gray-900 mb-6">Expert Fence Builders in Calgary: Custom Wood, Vinyl & Steel Fencing</h2>
            <p className="text-lg text-gray-600 leading-relaxed font-text">
              Busy Beaver Construction is a professional fence building company serving NW Calgary, SE Calgary, and surrounding areas. We specialize in the installation of custom wood, vinyl, chain link, and ornamental steel fencing. We design and construct durable, secure privacy fences engineered specifically to withstand Alberta&apos;s harsh freeze-thaw cycles and extreme wind conditions. Looking for complementary services? We are also a top-rated deck builder specializing in deck construction, pergolas, and retaining walls.
            </p>
          </div>

          {/* 28 Years Trust Section */}
          <div className="bg-white rounded-xl shadow-md p-8 md:p-12 mb-16 border-l-4 border-beaver-green flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-2/3">
              <h3 className="text-3xl font-secondary font-bold text-gray-800 mb-4">28 Years as Calgary&apos;s Trusted Fence Company</h3>
              <p className="text-gray-600 leading-relaxed font-text">
                Operating continuously since 1999, Busy Beaver Construction brings over 28 Years of local outdoor construction experience to every fencing project. Our family-owned operation relies strictly on dedicated, in-house installation crews rather than transient subcontractors, ensuring consistent quality control from the initial property line survey and post-hole digging to the final site cleanup. We maintain an A+ rating with the Better Business Bureau, have stellar reviews on Google and HomeStars, and hold comprehensive municipal licensing and liability insurance. We back our work with a solid warranty.
              </p>
            </div>
            <div className="md:w-1/3 flex justify-center">
              <img 
                src="/assets/images/2021/02/bbb.png" 
                alt="BBB A+ Rating Busy Beaver Construction" 
                className="w-48 h-auto object-contain opacity-90"
              />
            </div>
          </div>

          {/* Material Options - Visual Cards */}
          <div className="mb-16">
            <h3 className="text-3xl font-secondary font-bold text-gray-800 mb-8 text-center">Custom Fence Options for Calgary Weather</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              
              <div className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-shadow duration-300 p-8 border border-gray-100 flex flex-col h-full">
                <div className="w-14 h-14 bg-green-100 text-beaver-green rounded-full flex items-center justify-center mb-6">
                  <i className="fas fa-tree text-2xl"></i>
                </div>
                <h4 className="text-xl font-secondary font-bold text-gray-900 mb-4">Cedar & Pressure Treated Wood</h4>
                <p className="text-gray-600 leading-relaxed font-text flex-grow">
                  Wood fences remain the standard for residential privacy. Pressure-treated wood offers an economical, highly durable solution, infused with ACQ preservatives to resist rot. Western Red Cedar provides a premium, naturally rot-resistant alternative with a distinct, upscale aesthetic. Standard privacy fences utilize sturdy 4x4 or heavy-duty 4x6 posts depending on local wind exposure. We also build robust agricultural fences for larger properties.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-shadow duration-300 p-8 border border-gray-100 flex flex-col h-full">
                <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-6">
                  <i className="fas fa-shield-alt text-2xl"></i>
                </div>
                <h4 className="text-xl font-secondary font-bold text-gray-900 mb-4">Low-Maintenance Vinyl & Composite Fencing</h4>
                <p className="text-gray-600 leading-relaxed font-text flex-grow">
                  Vinyl fencing delivers complete privacy without recurring maintenance. High-quality vinyl panels resist UV degradation, preventing fading or cracking under Calgary&apos;s intense summer sun. We also offer composite fence options. These systems require no staining or painting and easily wash clean. We install solid panels, decorative picket tops, and lattice-top designs.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-shadow duration-300 p-8 border border-gray-100 flex flex-col h-full">
                <div className="w-14 h-14 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center mb-6">
                  <i className="fas fa-layer-group text-2xl"></i>
                </div>
                <h4 className="text-xl font-secondary font-bold text-gray-900 mb-4">Ornamental Steel & Aluminum</h4>
                <p className="text-gray-600 leading-relaxed font-text flex-grow">
                  For robust security and high-end curb appeal, we install the Ameristar Montage line of ornamental steel and aluminum fences. Featuring an E-coat finish, it provides the elegant appearance of wrought iron without rust. For properties backing onto Seton or Auburn Bay green spaces, heavy-gauge chain link fencing provides an economical containment solution.
                </p>
              </div>

            </div>
          </div>

          {/* Technical Info Split Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
            <div>
              <h3 className="text-2xl font-secondary font-bold text-gray-800 mb-4">Engineering for the Alberta Climate</h3>
              <p className="text-gray-600 leading-relaxed font-text mb-4">
                A fence is only as strong as its foundation. Calgary&apos;s frost line drops deep during the winter months, causing ground heave. If posts are not set below this active frost line, the structure will warp. We drill post holes to a minimum depth of 36 to 48 inches, filling them with high-PSI concrete.
              </p>
              <p className="text-gray-600 leading-relaxed font-text">
                Calgary&apos;s topography presents unique challenges. Homes in NW Calgary often sit on rocky, compacted glacial till, requiring specialized heavy machinery. Conversely, communities in SE Calgary feature heavy clay soils that retain water and expand aggressively. Our crews assess your specific soil conditions to determine exact footing depths.
              </p>
            </div>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-secondary font-bold text-gray-800 mb-4">Custom Gate Installation</h3>
                <p className="text-gray-600 leading-relaxed font-text">
                  Gates are the primary failure point of any perimeter fence. A poorly engineered gate will sag within six months. We over-engineer our gate installation. Standard pedestrian gates are built with heavy-duty, powder-coated hinges and diagonal anti-sag bracing. We also construct custom double-swing RV gates utilizing reinforced framing to manage extreme sheer weight.
                </p>
              </div>
              <div>
                <h3 className="text-2xl font-secondary font-bold text-gray-800 mb-4">City of Calgary Building Codes</h3>
                <p className="text-gray-600 leading-relaxed font-text">
                  Generally, residential backyard fences can be built up to 2 meters (approx 6 feet 6 inches) high without a municipal building permit from the City of Calgary. Front yard fences are restricted to 1.2 meters (4 feet). Corner lots have specific line-of-sight restrictions. Our estimators ensure your design complies fully with local building codes before construction begins.
                </p>
              </div>
            </div>
          </div>

          {/* Process & Financing UI */}
          <div className="bg-gray-900 text-white rounded-xl shadow-lg p-8 md:p-12 mb-16">
            <h3 className="text-3xl font-secondary font-bold mb-8 text-center">Our Building Process & Financing</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
              {/* Step 1 */}
              <div className="bg-gray-800 p-6 rounded-lg text-center relative z-10">
                <div className="w-12 h-12 bg-beaver-green text-black font-bold text-xl rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-gray-900">1</div>
                <h4 className="font-bold text-lg mb-2">Free Estimate</h4>
                <p className="text-gray-400 text-sm">On-site property measurement, grade checks, and fence quote generation.</p>
              </div>
              {/* Step 2 */}
              <div className="bg-gray-800 p-6 rounded-lg text-center relative z-10">
                <div className="w-12 h-12 bg-beaver-green text-black font-bold text-xl rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-gray-900">2</div>
                <h4 className="font-bold text-lg mb-2">Utility Locates</h4>
                <p className="text-gray-400 text-sm">We coordinate with Utility Safety Partners to locate underground lines.</p>
              </div>
              {/* Step 3 */}
              <div className="bg-gray-800 p-6 rounded-lg text-center relative z-10">
                <div className="w-12 h-12 bg-beaver-green text-black font-bold text-xl rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-gray-900">3</div>
                <h4 className="font-bold text-lg mb-2">Fence Replacement</h4>
                <p className="text-gray-400 text-sm">Safe demolition of old fence, deep post setting, and structural framing.</p>
              </div>
              {/* Step 4 */}
              <div className="bg-gray-800 p-6 rounded-lg text-center relative z-10">
                <div className="w-12 h-12 bg-beaver-green text-black font-bold text-xl rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-gray-900">4</div>
                <h4 className="font-bold text-lg mb-2">Inspection</h4>
                <p className="text-gray-400 text-sm">Thorough site clean-up and a final walkthrough to ensure satisfaction.</p>
              </div>
            </div>

            <div className="mt-10 text-center bg-gray-800 p-6 rounded-lg border border-gray-700">
              <p className="text-gray-300 font-text mb-4">
                To make backyard renovations accessible, we offer competitive fence cost Calgary options and partner with Financeit for fence financing. Homeowners can finance their new fence for as low as $99 a month, with flexible payment plans.
              </p>
              <a href="/fence-estimate" onClick={(e) => handleNavigation(e, '/fence-estimate')} className="inline-block bg-beaver-green text-black font-bold py-3 px-8 rounded hover:bg-white transition-colors duration-300 uppercase tracking-wider text-sm">
                Calculate Monthly Payments
              </a>
            </div>
          </div>

          {/* Interactive FAQ Accordion with H3 questions */}
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl font-secondary font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h3>
            <div className="space-y-4">
              
              <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                  <h3 className="font-bold text-gray-800 text-lg m-0">Do I need a building permit for a new fence in Calgary?</h3>
                  <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                    <i className="fas fa-chevron-down"></i>
                  </span>
                </summary>
                <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                  You do not need a building permit for a residential backyard fence if it is under 2 meters (6 feet 6 inches) in height. Fences in the front yard must not exceed 1.2 meters (4 feet). If your design exceeds these heights, or if you live on a corner lot with visibility restrictions, a permit is legally required.
                </div>
              </details>

              <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                  <h3 className="font-bold text-gray-800 text-lg m-0">How deep do fence posts need to be in Alberta?</h3>
                  <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                    <i className="fas fa-chevron-down"></i>
                  </span>
                </summary>
                <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                  Fence posts in Calgary must be set below the frost line to prevent aggressive frost heave. We drill post holes to a minimum depth of 36 to 48 inches and secure them with high-strength concrete, ensuring the fence remains plumb and stable during extreme temperature fluctuations.
                </div>
              </details>

              <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                  <h3 className="font-bold text-gray-800 text-lg m-0">Who is responsible for the cost of a shared property line fence?</h3>
                  <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                    <i className="fas fa-chevron-down"></i>
                  </span>
                </summary>
                <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                  In Alberta, if a fence is built exactly on the property line, it is considered a shared asset. Neighbors typically split the cost 50/50. However, mutual agreement must be reached before construction. If a neighbor declines to pay, you must build the fence entirely within your own property line at your own expense.
                </div>
              </details>
              
              <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                  <h3 className="font-bold text-gray-800 text-lg m-0">How far back from the property line does a fence need to be built?</h3>
                  <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                    <i className="fas fa-chevron-down"></i>
                  </span>
                </summary>
                <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                  If you are not splitting the cost with a neighbor to build on the shared line, your fence must be built entirely within your own property boundaries. There is no mandatory setback distance, but you must ensure footings do not encroach over the line. A real property report (RPR) is recommended to locate property pins accurately.
                </div>
              </details>

              <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                  <h3 className="font-bold text-gray-800 text-lg m-0">What is the most durable fence material for Calgary weather?</h3>
                  <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                    <i className="fas fa-chevron-down"></i>
                  </span>
                </summary>
                <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                  Ornamental steel and high-quality vinyl are the most durable, low-maintenance options for Calgary. For wood fences, Western Red Cedar naturally resists moisture and rot far better than untreated wood, though pressure-treated wood offers excellent long-term durability when properly maintained and stained.
                </div>
              </details>

              <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                  <h3 className="font-bold text-gray-800 text-lg m-0">Do Cedar fences need to be treated immediately after installation?</h3>
                  <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                    <i className="fas fa-chevron-down"></i>
                  </span>
                </summary>
                <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                  No. New Western Red Cedar needs time to cure and weather before applying treatments. We advise homeowners to wait at least 6 to 8 weeks during the dry summer months. Staining the wood immediately can trap internal moisture, causing the stain to blister and peel prematurely.
                </div>
              </details>
              
              <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                  <h3 className="font-bold text-gray-800 text-lg m-0">How do you build fences to withstand Calgary wind storms?</h3>
                  <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                    <i className="fas fa-chevron-down"></i>
                  </span>
                </summary>
                <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                  We mitigate wind shear by utilizing heavy-duty 4x4 or 4x6 posts set deep in high-PSI concrete footings. For solid wood or vinyl privacy fences built in high-wind corridors, we adjust the post spacing and structural framework to prevent panel blowout during extreme gusts.
                </div>
              </details>

              <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                  <h3 className="font-bold text-gray-800 text-lg m-0">How long does it take to build a residential fence?</h3>
                  <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                    <i className="fas fa-chevron-down"></i>
                  </span>
                </summary>
                <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                  A standard residential wood or vinyl fence takes between 3 to 5 days to complete, depending on total linear footage, soil conditions, and weather. This timeline includes the safe demolition of the old fence, post setting, necessary concrete curing time, and final panel installation.
                </div>
              </details>
              
              <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                  <h3 className="font-bold text-gray-800 text-lg m-0">How much does it cost to build a fence in Calgary?</h3>
                  <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                    <i className="fas fa-chevron-down"></i>
                  </span>
                </summary>
                <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                  Fence cost in Calgary varies widely based on linear footage, material choice, and soil conditions. Chain link is the most economical, followed by pressure-treated wood, cedar, vinyl, and ornamental steel. We provide a free estimate and fence calculator consultation to give you an exact quote for your specific project. For more details, explore our <a href="/blog/fence-installation-costs-calgary" onClick={(e) => handleNavigation(e, '/blog/fence-installation-costs-calgary')} className="text-beaver-green font-semibold hover:underline">Calgary fence cost guide</a>.
                </div>
              </details>

              <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                  <h3 className="font-bold text-gray-800 text-lg m-0">Do you offer fence financing?</h3>
                  <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                    <i className="fas fa-chevron-down"></i>
                  </span>
                </summary>
                <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                  Yes, we partner with Financeit to offer flexible fence financing options for homeowners. You can finance your new fence installation for as low as $99 a month, making backyard upgrades highly accessible.
                </div>
              </details>

            </div>
          </div>

        </div>
      </section>

      {/* Gallery Content */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center mb-8 md:mb-14">
            <h2 className="text-center text-5xl lg:text-6xl font-primary">Fence & Gate Gallery</h2>
          </div>
          
          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {displayedImages.map((image, index) => (
              <div 
                key={index} 
                className="overflow-hidden rounded shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <div className="h-[280px] md:h-[320px]">
                  <img 
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover loading-lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-500">{image.alt}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* No results message */}
          {displayedImages.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-700">Loading images...</p>
            </div>
          )}
          
          <div className="text-center mt-12">
            <p className="mb-6 text-gray-700">
              Ready to enhance your property with a quality fence? Contact us for a free estimate.
            </p>
            <a
              href="/fence-estimate" 
              className="inline-block px-6 py-3 bg-beaver-green text-black font-medium rounded hover:bg-beaver-green-dark hover:brightness-110 transition-all duration-300"
              onClick={(e) => handleNavigation(e, '/fence-estimate')}
            >
              GET FREE FENCE ESTIMATE
            </a>
          </div>
        </div>
      </section>
      
      {/* WE DO DAM GOOD WORK Section */}
      <section className="py-10 bg-gray-900 text-white text-center">
        <h2 className="text-4xl md:text-5xl font-bold tracking-wide">
          WE DO <span className="text-red-600">DAM</span> GOOD WORK
        </h2>
      </section>
      
      {/* Associations and Footer */}
      <AssociationsAndSuppliers />
      <Footer />
      
      {/* Lightbox */}
      {lightboxOpen && displayedImages.length > 0 && (
        <div 
          className={`fixed inset-0 bg-black z-50 flex items-center justify-center transition-opacity duration-300 ${
            lightboxAnimation === 'entering' ? 'opacity-0' : 
            lightboxAnimation === 'exiting' ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <div 
            className="absolute inset-0"
            onClick={closeLightbox}
          />
          
          <div className="relative max-w-7xl w-full h-screen max-h-screen flex items-center justify-center p-4">
            {/* Close button */}
            <button 
              onClick={closeLightbox}
              className={`absolute top-4 right-4 z-10 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                lightboxAnimation === 'entering' ? 'opacity-0 translate-y-[-20px]' : 
                lightboxAnimation === 'exiting' ? 'opacity-0 translate-y-[-20px]' : 'opacity-100 translate-y-0'
              }`}
              aria-label="Close lightbox"
            >
              <i className="fas fa-times text-2xl"></i>
            </button>
            
            {/* Image container */}
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={displayedImages[lightboxSlide].src}
                alt={displayedImages[lightboxSlide].alt}
                className={`max-w-full max-h-full object-contain transition-all duration-300 ${
                  lightboxAnimation === 'entering' ? 'opacity-0 scale-90' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 scale-90' : 'opacity-100 scale-100'
                }`}
              />
              
              {/* Image caption */}
              <div 
                className={`absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white py-2 px-4 rounded text-center max-w-lg transition-all duration-300 ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-y-[20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-y-[20px]' : 'opacity-100 translate-y-0'
                }`}
              >
                {displayedImages[lightboxSlide].alt}
              </div>
              
              {/* Navigation buttons */}
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToPrevLightboxSlide()
                }}
                className={`absolute left-[0px] md:left-[60px] lg:left-[-180px] top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-x-[-20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-x-[-20px]' : 'opacity-100 translate-x-0'
                }`}
                aria-label="Previous slide"
              >
                <i className="fas fa-chevron-left text-2xl"></i>
              </button>
              
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToNextLightboxSlide()
                }}
                className={`absolute right-[0px] md:right-[60px] lg:right-[-180px] top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-x-[20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-x-[20px]' : 'opacity-100 translate-x-0'
                }`}
                aria-label="Next slide"
              >
                <i className="fas fa-chevron-right text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}